import { useEffect, useRef, useState } from "react";
import api, { fmtDate } from "@/lib/api";
import { toast } from "sonner";
import {
  X, Plus, Trash2, Image as ImageIcon, Sun, Cloud, CloudRain, Wind, Snowflake, Thermometer, CloudLightning,
  Users, AlertTriangle, ArrowRight, FileText, Loader2, Camera, Edit3, Download,
} from "lucide-react";

const WEATHER = [
  { value: "soleado", label: "Soleado", icon: Sun, color: "#f59e0b" },
  { value: "nublado", label: "Nublado", icon: Cloud, color: "#64748b" },
  { value: "lluvia", label: "Lluvia", icon: CloudRain, color: "#0891b2" },
  { value: "tormenta", label: "Tormenta", icon: CloudLightning, color: "#7c3aed" },
  { value: "calor", label: "Calor", icon: Thermometer, color: "#dc2626" },
  { value: "frio", label: "Frío", icon: Snowflake, color: "#0ea5e9" },
  { value: "viento", label: "Viento", icon: Wind, color: "#475569" },
];
const WEATHER_BY = Object.fromEntries(WEATHER.map((w) => [w.value, w]));

export default function BitacoraPanel({ projectId, projectCode, onClose, currentUser }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null); // entry object
  const [downloading, setDownloading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/bitacora`);
      setEntries(data.entries || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al cargar bitácora");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  const canEdit = (entry) => currentUser?.role === "admin" || entry.created_by === currentUser?.id;

  const remove = async (entry) => {
    if (!window.confirm("¿Eliminar esta entrada de bitácora?")) return;
    try {
      await api.delete(`/bitacora/${entry.id}`);
      toast.success("Entrada eliminada");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al eliminar"); }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/projects/${projectId}/bitacora/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `bitacora-${projectCode || projectId}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error("No se pudo generar PDF"); }
    finally { setDownloading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="bitacora-panel">
      <div className="flex-1" style={{ background: "rgba(2,6,14,0.7)" }} onClick={onClose} />
      <div className="w-full max-w-2xl flex flex-col" style={{ background: "#0a0f1d", borderLeft: "1px solid var(--border-2)", maxHeight: "100dvh" }}>
        {/* Header */}
        <div className="px-5 py-4 flex items-center justify-between sticky top-0 z-10" style={{ background: "var(--bg-2)", borderBottom: "1px solid var(--border)" }}>
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-sm flex items-center justify-center" style={{ background: "rgba(217,70,239,0.18)", border: "1px solid var(--magenta)" }}>
              <FileText className="w-4 h-4" style={{ color: "var(--magenta)" }} />
            </div>
            <div>
              <div className="label-mono" style={{ color: "var(--magenta)" }}>// BITÁCORA DE OBRA</div>
              <div className="font-display font-black text-slate-100">{projectCode}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="bitacora-pdf-btn" onClick={downloadPdf} disabled={downloading || entries.length === 0}
              className="btn-ghost px-3 py-1.5 rounded-sm text-xs uppercase tracking-widest font-semibold flex items-center gap-1.5 disabled:opacity-40">
              {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} PDF
            </button>
            <button data-testid="bitacora-new-btn" onClick={() => { setEditing(null); setOpenForm(true); }}
              className="btn-primary px-3 py-1.5 rounded-sm text-xs uppercase tracking-widest font-semibold flex items-center gap-1.5">
              <Plus className="w-3.5 h-3.5" /> Nueva
            </button>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-100"><X className="w-5 h-5" /></button>
          </div>
        </div>

        {/* Feed */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {loading && <div className="text-center py-12 text-slate-500">Cargando…</div>}
          {!loading && entries.length === 0 && (
            <div className="text-center py-16">
              <FileText className="w-10 h-10 mx-auto mb-2 text-slate-700" />
              <div className="text-slate-500 mb-3">Sin entradas todavía.</div>
              <button onClick={() => { setEditing(null); setOpenForm(true); }} className="btn-primary px-3 py-1.5 rounded-sm text-xs uppercase tracking-widest font-semibold">
                Registrar primera entrada
              </button>
            </div>
          )}
          {!loading && entries.map((e) => <EntryCard key={e.id} entry={e} canEdit={canEdit(e)} onEdit={() => { setEditing(e); setOpenForm(true); }} onDelete={() => remove(e)} />)}
        </div>
      </div>

      {openForm && (
        <BitacoraForm
          projectId={projectId}
          editing={editing}
          onClose={() => setOpenForm(false)}
          onSaved={() => { setOpenForm(false); load(); }}
        />
      )}
    </div>
  );
}

function EntryCard({ entry, canEdit, onEdit, onDelete }) {
  const w = entry.weather ? WEATHER_BY[entry.weather] : null;
  return (
    <div data-testid={`bitacora-entry-${entry.id}`} className="panel p-4" style={{ borderLeft: "3px solid var(--magenta)" }}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <div className="font-display font-bold text-slate-100 text-base">{fmtDate(entry.entry_date)}</div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            {entry.created_by_name || "—"} · <span className="uppercase">{entry.created_by_role || ""}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {w && (
            <span className="text-[10px] uppercase tracking-widest font-mono flex items-center gap-1 px-2 py-1 rounded-sm" style={{ color: w.color, border: `1px solid ${w.color}`, background: "rgba(0,0,0,0.2)" }}>
              <w.icon className="w-3 h-3" /> {w.label}
            </span>
          )}
          {entry.workers_count != null && (
            <span className="text-[10px] uppercase tracking-widest font-mono flex items-center gap-1 px-2 py-1 rounded-sm" style={{ color: "var(--cyan)", border: "1px solid var(--cyan)", background: "rgba(0,0,0,0.2)" }}>
              <Users className="w-3 h-3" /> {entry.workers_count}
            </span>
          )}
          {entry.progress_pct != null && (
            <span className="text-[10px] uppercase tracking-widest font-mono px-2 py-1 rounded-sm" style={{ color: "var(--green)", border: "1px solid var(--green)", background: "rgba(0,0,0,0.2)" }}>
              {entry.progress_pct}%
            </span>
          )}
        </div>
      </div>

      {entry.progress && (
        <div className="mb-2 text-sm text-slate-200 whitespace-pre-wrap">{entry.progress}</div>
      )}

      {entry.incidents && (
        <div className="mb-2 px-3 py-2 rounded-sm text-xs flex items-start gap-2" style={{ background: "rgba(245,158,11,0.10)", border: "1px solid var(--amber)" }}>
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: "var(--amber)" }} />
          <div><div className="label-mono mb-0.5" style={{ color: "var(--amber)" }}>INCIDENTES</div><div className="text-slate-300 whitespace-pre-wrap">{entry.incidents}</div></div>
        </div>
      )}

      {entry.next_activities && (
        <div className="mb-2 px-3 py-2 rounded-sm text-xs flex items-start gap-2" style={{ background: "rgba(34,211,238,0.08)", border: "1px solid var(--cyan)" }}>
          <ArrowRight className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: "var(--cyan)" }} />
          <div><div className="label-mono mb-0.5" style={{ color: "var(--cyan)" }}>PRÓXIMAS ACTIVIDADES</div><div className="text-slate-300 whitespace-pre-wrap">{entry.next_activities}</div></div>
        </div>
      )}

      {entry.photo_ids && entry.photo_ids.length > 0 && (
        <div className="flex gap-2 overflow-x-auto mt-2 pb-1">
          {entry.photo_ids.map((pid) => (
            <a key={pid} href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${pid}`} target="_blank" rel="noreferrer">
              <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${pid}`} alt="evidencia" className="w-16 h-16 object-cover rounded-sm" style={{ border: "1px solid var(--border-2)" }} />
            </a>
          ))}
        </div>
      )}

      {canEdit && (
        <div className="flex gap-2 justify-end mt-2 pt-2" style={{ borderTop: "1px dashed var(--border)" }}>
          <button data-testid={`bitacora-edit-${entry.id}`} onClick={onEdit} className="text-[10px] uppercase tracking-widest font-semibold hover:underline flex items-center gap-1" style={{ color: "var(--cyan)" }}><Edit3 className="w-3 h-3" /> Editar</button>
          <button data-testid={`bitacora-delete-${entry.id}`} onClick={onDelete} className="text-[10px] uppercase tracking-widest font-semibold hover:underline flex items-center gap-1" style={{ color: "var(--red)" }}><Trash2 className="w-3 h-3" /> Eliminar</button>
        </div>
      )}
    </div>
  );
}

function BitacoraForm({ projectId, editing, onClose, onSaved }) {
  const isEdit = !!editing;
  const [form, setForm] = useState({
    project_id: projectId,
    entry_date: editing?.entry_date || new Date().toISOString().slice(0, 10),
    progress: editing?.progress || "",
    progress_pct: editing?.progress_pct ?? "",
    weather: editing?.weather || "",
    workers_count: editing?.workers_count ?? "",
    incidents: editing?.incidents || "",
    next_activities: editing?.next_activities || "",
    photo_ids: editing?.photo_ids || [],
  });
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const photoRef = useRef();

  const addPhotos = async (files) => {
    if (!files || files.length === 0) return;
    setUploadingPhotos(true);
    const newIds = [];
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        newIds.push(data.id);
      } catch (e) { toast.error(`Error al subir ${f.name}`); }
    }
    setForm((f) => ({ ...f, photo_ids: [...f.photo_ids, ...newIds] }));
    setUploadingPhotos(false);
  };

  const removePhoto = (pid) => setForm((f) => ({ ...f, photo_ids: f.photo_ids.filter((x) => x !== pid) }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.progress.trim()) { toast.error("El avance del día es requerido"); return; }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (payload.weather === "") delete payload.weather;
      if (payload.workers_count === "" || payload.workers_count == null) delete payload.workers_count;
      else payload.workers_count = Number(payload.workers_count);
      if (payload.progress_pct === "" || payload.progress_pct == null) delete payload.progress_pct;
      else payload.progress_pct = Number(payload.progress_pct);
      if (!payload.incidents) delete payload.incidents;
      if (!payload.next_activities) delete payload.next_activities;
      if (isEdit) {
        await api.patch(`/bitacora/${editing.id}`, payload);
        toast.success("Entrada actualizada");
      } else {
        payload.client_op_id = (window.crypto?.randomUUID && window.crypto.randomUUID()) || `op-${Date.now()}`;
        await api.post(`/projects/${projectId}/bitacora`, payload);
        toast.success("Entrada registrada");
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] p-4 flex items-center justify-center" style={{ background: "rgba(2,6,14,0.85)", backdropFilter: "blur(6px)" }} onClick={onClose}>
      <div className="w-full max-w-lg overflow-y-auto rounded-md shadow-2xl" style={{ background: "#0a0f1d", border: "1px solid var(--magenta)", maxHeight: "calc(100dvh - 2rem)" }} onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 flex items-center justify-between sticky top-0 z-10" style={{ background: "var(--bg-2)", borderBottom: "1px solid var(--border)" }}>
          <div>
            <div className="label-mono" style={{ color: "var(--magenta)" }}>// {isEdit ? "EDITAR" : "NUEVA"} ENTRADA</div>
            <h2 className="font-display font-bold text-xl text-slate-100 tracking-wide">Bitácora de obra</h2>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-cyan-300"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={save} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-mono block mb-1.5">Fecha</label>
              <input type="date" required value={form.entry_date} disabled={isEdit}
                onChange={(e) => setForm({ ...form, entry_date: e.target.value })} className="w-full input-cyber px-3 py-2 text-sm" data-testid="bita-date" />
            </div>
            <div>
              <label className="label-mono block mb-1.5">% Avance acumulado</label>
              <input type="number" min="0" max="100" value={form.progress_pct} placeholder="opcional"
                onChange={(e) => setForm({ ...form, progress_pct: e.target.value })} className="w-full input-cyber px-3 py-2 text-sm" data-testid="bita-pct" />
            </div>
          </div>

          <div>
            <label className="label-mono block mb-1.5">Avance del día *</label>
            <textarea required value={form.progress} rows={4} placeholder="Describe qué se hizo hoy: colado de losa, armado, instalaciones, etc."
              onChange={(e) => setForm({ ...form, progress: e.target.value })} className="w-full input-cyber px-3 py-2 text-sm" data-testid="bita-progress" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-mono block mb-1.5">Clima</label>
              <select value={form.weather} data-testid="bita-weather"
                onChange={(e) => setForm({ ...form, weather: e.target.value })} className="w-full input-cyber px-3 py-2 text-sm">
                <option value="" style={{ background: "#0a0f1d" }}>— Sin especificar —</option>
                {WEATHER.map((w) => <option key={w.value} value={w.value} style={{ background: "#0a0f1d" }}>{w.label}</option>)}
              </select>
            </div>
            <div>
              <label className="label-mono block mb-1.5">Personal en obra</label>
              <input type="number" min="0" value={form.workers_count} placeholder="# trabajadores"
                onChange={(e) => setForm({ ...form, workers_count: e.target.value })} className="w-full input-cyber px-3 py-2 text-sm" data-testid="bita-workers" />
            </div>
          </div>

          <div>
            <label className="label-mono block mb-1.5">Incidentes / Observaciones</label>
            <textarea value={form.incidents} rows={2} placeholder="Accidentes, faltantes, retrasos, visitas, etc."
              onChange={(e) => setForm({ ...form, incidents: e.target.value })} className="w-full input-cyber px-3 py-2 text-sm" data-testid="bita-incidents" />
          </div>

          <div>
            <label className="label-mono block mb-1.5">Próximas actividades</label>
            <textarea value={form.next_activities} rows={2} placeholder="Mañana se planea…"
              onChange={(e) => setForm({ ...form, next_activities: e.target.value })} className="w-full input-cyber px-3 py-2 text-sm" data-testid="bita-next" />
          </div>

          {/* Photos */}
          <div>
            <label className="label-mono block mb-1.5">Fotografías de evidencia</label>
            <input ref={photoRef} type="file" accept="image/*" multiple capture="environment"
              onChange={(e) => { addPhotos(Array.from(e.target.files)); e.target.value = ""; }} className="hidden" data-testid="bita-photos-input" />
            <div className="flex flex-wrap items-center gap-2">
              {form.photo_ids.map((pid) => (
                <div key={pid} className="relative">
                  <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${pid}`} alt="" className="w-16 h-16 object-cover rounded-sm" style={{ border: "1px solid var(--border-2)" }} />
                  <button type="button" onClick={() => removePhoto(pid)} className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full flex items-center justify-center text-[10px]" style={{ background: "var(--red)", color: "white" }}>×</button>
                </div>
              ))}
              <button type="button" data-testid="bita-add-photo-btn" onClick={() => photoRef.current?.click()} disabled={uploadingPhotos}
                className="w-16 h-16 rounded-sm flex flex-col items-center justify-center gap-0.5 disabled:opacity-40" style={{ background: "var(--bg-2)", border: "1px dashed var(--border-2)" }}>
                {uploadingPhotos ? <Loader2 className="w-4 h-4 animate-spin text-slate-400" /> : <><Camera className="w-4 h-4 text-slate-400" /><span className="text-[9px] text-slate-500">Foto</span></>}
              </button>
            </div>
          </div>

          <div className="flex gap-2 justify-end pt-3 -mx-6 -mb-6 px-6 pb-6 sticky bottom-0 z-20"
            style={{ borderTop: "1px solid var(--border)", background: "#0a0f1d", paddingBottom: "calc(1.5rem + env(safe-area-inset-bottom))" }}>
            <button type="button" onClick={onClose} className="btn-ghost px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm">Cancelar</button>
            <button type="submit" disabled={submitting} data-testid="bita-save"
              className="btn-primary px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm disabled:opacity-50">
              {submitting ? "Guardando…" : (isEdit ? "Actualizar" : "Registrar")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
